/**
 * Утилита для выполнения запросов с fallback на прокси
 */

// Константы прокси URL для критически важных операций
const FALLBACK_PROXY_URLS = {
  supabaseEdgeFunctions: 'https://syngo-tt-supabase.tuk-tuk.rent/functions/v1', // URL прокси для Edge Functions
  supabaseApi: 'https://syngo-tt-supabase.tuk-tuk.rent', // URL прокси для API
  supabaseStorage: 'https://syngo-tt-supabase.tuk-tuk.rent' // URL прокси для Storage
};

// Резервная конфигурация прокси для критически важных сервисов
const FALLBACK_PROXY_CONFIG = {
  'VITE_SUPABASE_PROXY_ENABLED': 'true',
  'VITE_SUPABASE_PROXY_URL': 'https://syngo-tt-supabase.tuk-tuk.rent',
  'VITE_SUPABASE_PROXY_TIMEOUT': '30000',
  'VITE_SUPABASE_STORAGE_PROXY_ENABLED': 'true', 
  'VITE_SUPABASE_STORAGE_PROXY_URL': 'https://syngo-tt-supabase.tuk-tuk.rent',
  'VITE_SUPABASE_STORAGE_PROXY_TIMEOUT': '30000',
  'VITE_GOOGLE_MAPS_PROXY_ENABLED': 'false',
  'VITE_GOOGLE_MAPS_PROXY_URL': '',
  'VITE_GOOGLE_MAPS_PROXY_TIMEOUT': '15000',
  'VITE_GEOCODING_PROXY_ENABLED': 'false',
  'VITE_GEOCODING_PROXY_URL': '',
  'VITE_GEOCODING_PROXY_TIMEOUT': '15000',
  'VITE_MAKE_WEBHOOKS_PROXY_ENABLED': 'true',
  'VITE_MAKE_WEBHOOKS_PROXY_URL': 'https://syngo-tt-supabase.tuk-tuk.rent',
  'VITE_MAKE_WEBHOOKS_PROXY_TIMEOUT': '10000',
  'VITE_WEBHOOKS_PROXY_ENABLED': 'false',
  'VITE_WEBHOOKS_PROXY_URL': '',
  'VITE_WEBHOOKS_PROXY_TIMEOUT': '10000'
};

interface FallbackFetchOptions {
  method?: string;
  headers?: Record<string, string>;
  body?: string;
  timeout?: number;
}

/**
 * Выполняет запрос с fallback на прокси при ошибке
 */
export async function fallbackFetch(
  directUrl: string,
  proxyUrl: string,
  options: FallbackFetchOptions = {}
): Promise<Response> {
  const requestOptions: RequestInit = {
    method: options.method || 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers
    },
    ...(options.body && { body: options.body })
  };

  console.log('🔄 Попытка прямого запроса:', directUrl);

  try {
    // Первая попытка - прямой запрос
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), options.timeout || 10000);

    const response = await fetch(directUrl, {
      ...requestOptions,
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (response.ok) {
      console.log('✅ Прямой запрос успешен');
      return response;
    }

    console.warn('⚠️ Прямой запрос вернул ошибку:', response.status);
    throw new Error(`Direct request failed: ${response.status}`);
  } catch (error) {
    console.warn('❌ Прямой запрос не удался, пробуем прокси:', error);

    // Fallback на прокси
    if (proxyUrl && proxyUrl !== 'undefined' && proxyUrl.length > 0) {
      try {
        console.log('🔄 Попытка запроса через прокси:', proxyUrl);
        
        const proxyResponse = await fetch(proxyUrl, {
          ...requestOptions,
          headers: {
            ...requestOptions.headers,
            'X-Original-URL': directUrl,
            'X-Proxy-Target': 'supabase-functions'
          }
        });

        if (proxyResponse.ok) {
          console.log('✅ Запрос через прокси успешен');
          return proxyResponse;
        }

        console.error('❌ Запрос через прокси тоже не удался:', proxyResponse.status);
        throw new Error(`Proxy request failed: ${proxyResponse.status}`);
      } catch (proxyError) {
        console.error('❌ Ошибка прокси-запроса:', proxyError);
        throw proxyError;
      }
    } else {
      console.error('❌ Прокси URL не настроен, не можем использовать fallback');
      throw error;
    }
  }
}

/**
 * Специальная функция для вызова Supabase Edge Functions с fallback и улучшенной обработкой ошибок
 */
export async function invokeSupabaseFunctionWithFallback(
  functionName: string,
  payload: any = {},
  authToken?: string
): Promise<any> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const directUrl = `${supabaseUrl}/functions/v1/${functionName}`;
  const proxyUrl = `${FALLBACK_PROXY_URLS.supabaseEdgeFunctions}/${functionName}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
  };

  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  try {
    // Для функции get-env-variables используем более короткий таймаут и быстрый fallback
    const timeout = functionName === 'get-env-variables' ? 5000 : 15000;
    
    const response = await fallbackFetch(directUrl, proxyUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
      timeout
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`❌ Не удалось вызвать функцию ${functionName}:`, error);
    
    // Для get-env-variables возвращаем резервную конфигурацию
    if (functionName === 'get-env-variables') {
      console.log('🔄 Используем резервную конфигурацию прокси');
      return { variables: FALLBACK_PROXY_CONFIG };
    }
    
    throw error;
  }
}

export { FALLBACK_PROXY_URLS, FALLBACK_PROXY_CONFIG };
